import { AccountTypeValue } from '../types';
import { GetUsersParams } from '../services/UserService';
import { DashboardParams } from '../services/DashboardService';
import { ReportDeliveryQueueParams } from '../services/ResultService';
import { ReportTemplatePreviewBody } from '../services/ReportTemplateService';

const usersKey = 'users';
const classroomsKey = 'classrooms';
const demoClassroomKey = 'demo-classroom';
const dashboardKey = 'dashboard';
const reportTemplatesKey = 'report-templates';
const reportDeliveryQueueKey = 'report-delivery-queue';
const subjectsKey = 'subjects';
const gradebookKey = 'gradebook';
const attendanceKey = 'attendance';

export const queryKeys = {
    users: {
        all: [usersKey] as const,
        list: (type: AccountTypeValue | null, params: GetUsersParams = {}) => [
            usersKey,
            'list',
            type,
            params.sessionId ?? null,
            params.termId ?? null,
            params.skip ?? 0,
            params.limit ?? 10,
        ] as const,
        profile: (userId: string, termId?: string | null, sessionId?: string | null) => [
            usersKey,
            'profile',
            userId,
            termId ?? null,
            sessionId ?? null,
        ] as const,
    },
    classrooms: {
        all: [classroomsKey] as const,
        list: (
            skip: number = 0,
            limit: number = 50,
            sessionId?: string | null,
            termId?: string | null,
        ) => [
            classroomsKey,
            'list',
            skip,
            limit,
            sessionId ?? null,
            termId ?? null,
        ] as const,
        detail: (classroomId: string) => [
            classroomsKey,
            'detail',
            classroomId,
        ] as const,
    },
    demoClassroom: {
        status: [demoClassroomKey, 'status'] as const,
        guardianPreview: [demoClassroomKey, 'guardian-preview'] as const,
    },
    dashboard: {
        all: [dashboardKey] as const,
        school: (schoolId: string, params: DashboardParams = {}) => [
            dashboardKey,
            'school',
            schoolId,
            params.termId ?? null,
            params.classroomId ?? null,
            params.subjectId ?? null,
            params.category ?? null,
            params.performerOrder ?? null,
        ] as const,
        teacherAnalytics: (schoolId: string, params: DashboardParams = {}) => [
            dashboardKey,
            'teacher-analytics',
            schoolId,
            params.termId ?? null,
            params.classroomId ?? null,
            params.subjectId ?? null,
            params.category ?? null,
            params.performerOrder ?? null,
        ] as const,
    },
    reportTemplates: {
        all: [reportTemplatesKey] as const,
        list: () => [reportTemplatesKey, 'list'] as const,
        detail: (id: string) => [reportTemplatesKey, 'detail', id] as const,
        preview: (body: ReportTemplatePreviewBody) => [
            reportTemplatesKey,
            'preview',
            JSON.stringify(body),
        ] as const,
    },
    reportDeliveryQueue: {
        all: [reportDeliveryQueueKey] as const,
        list: (params: ReportDeliveryQueueParams) => [
            reportDeliveryQueueKey,
            'list',
            params.termId,
            params.classroomId ?? null,
            params.channel ?? null,
            params.deliveryStatus ?? null,
            params.skip ?? 0,
            params.limit ?? 50,
        ] as const,
    },
    subjects: {
        tags: (classroomId?: string | null) => [
            subjectsKey,
            'tags',
            classroomId ?? 'school',
        ] as const,
    },
    gradebook: {
        all: [gradebookKey] as const,
        detail: (
            classroomId: string,
            termId: string,
            subjectId?: string | null,
            rubricId?: string | null,
        ) => [
            gradebookKey,
            'detail',
            classroomId,
            termId,
            subjectId ?? null,
            rubricId ?? null,
        ] as const,
        classroomTermPrefix: (classroomId: string, termId: string) => [
            gradebookKey,
            'detail',
            classroomId,
            termId,
        ] as const,
        classroomPrefix: (classroomId: string) => [
            gradebookKey,
            'detail',
            classroomId,
        ] as const,
    },
    attendance: {
        all: [attendanceKey] as const,
        detail: (classroomId: string, termId: string) => [
            attendanceKey,
            'detail',
            classroomId,
            termId,
        ] as const,
    },
};
